/**
 * useProductInfo Hook
 *
 * Fetches the first message from a conversation and extracts MOD product info.
 * Prefers structured productIds from message attributes over text parsing.
 */

import { useQuery } from '@tanstack/react-query';

import type { ChatConversation } from '@/services/chat/chatTransport';
import {
  getProductDisplayInfo,
  getProductDisplayInfoFromIds,
  type ProductDisplayInfo,
} from '@/utils/modHelpers';

type FirstMessageData = {
  body: string;
  productIds?: string[];
};

type UseProductInfoResult = {
  productDisplay: ProductDisplayInfo | null;
  isLoading: boolean;
};

/**
 * Hook to extract MOD product information from a conversation
 *
 * Fetches the first message and extracts product display info.
 * Prefers structured productIds from message attributes when available,
 * falls back to text parsing from message body.
 *
 * @param conversation - Twilio conversation to extract product info from
 * @returns Product display info (or null) and loading state
 */
export function useProductInfo(conversation: ChatConversation): UseProductInfoResult {
  const { data: firstMessageData, isLoading } = useQuery({
    queryKey: ['twilio', 'conversation', 'firstMessage', conversation.id],
    queryFn: async (): Promise<FirstMessageData> => {
      const messages = await conversation.getMessages(1, 0);

      const message = messages?.items[0];

      const attributes =
        message?.attributes && typeof message.attributes === 'object'
          ? (message.attributes as Record<string, unknown>)
          : undefined;

      const productIds = Array.isArray(attributes?.productIds)
        ? (attributes.productIds as string[])
        : undefined;

      return {
        body: message?.body || '',
        productIds,
      };
    },
  });

  const productDisplay =
    getProductDisplayInfoFromIds(firstMessageData?.productIds) ??
    getProductDisplayInfo(firstMessageData?.body);

  return {
    productDisplay,
    isLoading,
  };
}
